import { useCallback, useEffect, useMemo, useState } from 'react';
import { Zap, Mail, AlertTriangle, Clock } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { automationSettingsService } from '@core/supabase/services/automationSettingsService';
import type { OrgAutomationSettings } from '@core/supabase/services/automationSettingsService';
import { orgService } from '@core/supabase/services/orgService';
import { queryKeys } from '@core/query/queryKeys';
import { PermissionGate } from '@shared/components/ui/PermissionGate';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { useToast } from '@shared/hooks/useToast';

function Section({
  title,
  description,
  children,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="ggj-param-group overflow-hidden">
      <div className="ggj-profile-section-h">{title}</div>
      <div className="ggj-profile-section-body space-y-4">
        {description ? (
          <p className="ggj-text-body vscode-text-secondary leading-relaxed">{description}</p>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/**
 * Phase 8 · C-38 — Org-level default TAT for new positions.
 *
 * Reads / writes `ggj_b2b_organizations.default_tat_days` (migration 105).
 * Role-gated locally (owner/admin) even though the outer PermissionGate
 * already blocks non-admins; this way the rule survives any future
 * relaxation of the page gate. The input tooltip surfaces the same message
 * the spec calls for ("Requires admin role").
 */
function PositionDefaultsSection({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { toast, showToast } = useToast();

  const { data: serverDays, isLoading } = useQuery({
    queryKey: queryKeys.org.defaults(orgId),
    queryFn: () => orgService.getDefaultTatDays(orgId),
  });

  const [days, setDays] = useState<number>(30);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (typeof serverDays === 'number') setDays(serverDays);
  }, [serverDays]);

  const changed = useMemo(
    () => typeof serverDays === 'number' && days !== serverDays,
    [days, serverDays]
  );

  const disabledReason = canEdit ? undefined : 'Requires admin role';
  const inputDisabled = !canEdit || saving || isLoading;
  const buttonDisabled = !canEdit || saving || !changed || isLoading;

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await orgService.setDefaultTatDays(orgId, days);
      await qc.invalidateQueries({ queryKey: queryKeys.org.defaults(orgId) });
      showToast({ title: 'Default TAT updated', body: `New positions default to ${days} days.` });
    } catch (e) {
      showToast({
        title: 'Could not update default TAT',
        body: e instanceof Error ? e.message : 'Unexpected error.',
        kind: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [orgId, days, qc, showToast]);

  return (
    <Section
      title={
        <span className="inline-flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-400" /> Position defaults
        </span>
      }
      description="Applied to every new position that does not set an explicit closing date."
    >
      {toast}
      <div className="flex flex-col gap-1" title={disabledReason}>
        <label className="ggj-text-meta vscode-text-tertiary">Default position TAT</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={365}
            className="input w-32"
            value={days}
            disabled={inputDisabled}
            title={disabledReason}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (Number.isNaN(next)) return;
              setDays(Math.min(365, Math.max(1, Math.round(next))));
            }}
          />
          <span className="ggj-text-meta vscode-text-tertiary">days</span>
        </div>
        <p className="text-[11px] vscode-text-tertiary">Range 1–365. Applies to positions created from here on.</p>
      </div>
      <div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={buttonDisabled}
          title={disabledReason}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Section>
  );
}

/**
 * Phase 7 §2.11 — Domain-language automation templates (org settings).
 */
export function AutomationSettingsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const orgId = user?.orgId ?? null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [settings, setSettings] = useState<OrgAutomationSettings | null>(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const s = await automationSettingsService.getForOrg(orgId);
      setSettings({
        ...s,
        stuck_stage_enabled: s.stuck_stage_enabled ?? false,
        stuck_stage_days: s.stuck_stage_days ?? 7,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load automation settings.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback(
    async (partial: Partial<OrgAutomationSettings>) => {
      if (!orgId || !settings) return;
      setSaving(true);
      setError(null);
      setOk(false);
      try {
        const next = await automationSettingsService.update(orgId, partial);
        setSettings({
          ...next,
          stuck_stage_enabled: next.stuck_stage_enabled ?? false,
          stuck_stage_days: next.stuck_stage_days ?? 7,
        });
        setOk(true);
        setTimeout(() => setOk(false), 2500);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Save failed.');
      } finally {
        setSaving(false);
      }
    },
    [orgId, settings]
  );

  return (
    <PermissionGate allowed={perms.isAdmin || perms.isOwner} label="Automation settings">
      {!orgId ? (
        <p className="ggj-text-body vscode-text-secondary">No organisation context.</p>
      ) : loading || !settings ? (
        <FullPageSpinner />
      ) : (
      <div className="space-y-6">
        <div className="mb-1">
          <h2 className="text-[15px] font-semibold">Automation</h2>
          <p className="text-[12px] vscode-text-tertiary mt-0.5">Configure Smart Match thresholds and pipeline automation rules.</p>
        </div>
        {error ? (
          <div className="rounded-[10px] border border-red-800/50 bg-red-950/30 px-4 py-3 ggj-text-body text-red-300">{error}</div>
        ) : null}
        {ok ? (
          <div className="rounded-[10px] border border-emerald-800/40 bg-emerald-950/25 px-4 py-2 ggj-text-body text-emerald-300">
            Saved.
          </div>
        ) : null}

        <Section title={<span className="inline-flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Smart Match</span>}>
          <p className="ggj-text-body vscode-text-secondary leading-relaxed">
            When a new match score is saved, candidates at or above this threshold can be auto-shortlisted (creates an
            application if none exists). Uses <code className="ggj-text-meta">runPostMatchAutomation</code> after match
            persistence.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border vscode-border"
              checked={settings.auto_shortlist_enabled}
              disabled={saving}
              onChange={(e) => void patch({ auto_shortlist_enabled: e.target.checked })}
            />
            <span className="ggj-text-body vscode-text-primary">Auto-shortlist when match score ≥ threshold</span>
          </label>
          <div>
            <label className="ggj-text-meta vscode-text-tertiary">Minimum score (0–100)</label>
            <input
              type="number"
              min={0}
              max={100}
              className="input w-32 mt-1"
              value={settings.auto_shortlist_min_score}
              disabled={saving || !settings.auto_shortlist_enabled}
              onChange={(e) =>
                void patch({ auto_shortlist_min_score: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })
              }
            />
          </div>
        </Section>

        <Section title={<span className="inline-flex items-center gap-2"><Mail className="w-4 h-4 text-sky-400" /> After shortlist</span>}>
          <p className="ggj-text-body vscode-text-secondary leading-relaxed">
            When an application is auto-shortlisted (or shortlisted manually in flows that call the same hooks), optionally
            start your email sequence and/or schedule an AI screening interview.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border vscode-border"
              checked={settings.auto_email_on_shortlist}
              disabled={saving}
              onChange={(e) => void patch({ auto_email_on_shortlist: e.target.checked })}
            />
            <span className="ggj-text-body vscode-text-primary">Send / enroll email sequence on shortlist</span>
          </label>
          <p className="text-[11px] vscode-text-tertiary">
            Template: “When interview assessed ≥ threshold, send shortlist email” pairs with pipeline advance below so the
            candidate reaches a stage where your sequence applies.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border vscode-border"
              checked={settings.auto_interview_on_shortlist}
              disabled={saving}
              onChange={(e) => void patch({ auto_interview_on_shortlist: e.target.checked })}
            />
            <span className="ggj-text-body vscode-text-primary">Schedule AI screening interview on shortlist</span>
          </label>
        </Section>

        <Section title="Interview completion">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border vscode-border"
              checked={settings.auto_advance_on_interview_complete}
              disabled={saving}
              onChange={(e) => void patch({ auto_advance_on_interview_complete: e.target.checked })}
            />
            <span className="ggj-text-body vscode-text-primary">Auto-advance pipeline when AI interview completes</span>
          </label>
          <div>
            <label className="ggj-text-meta vscode-text-tertiary">Target status key</label>
            <input
              type="text"
              className="input w-full max-w-md mt-1 font-mono ggj-text-meta"
              value={settings.auto_advance_target_status}
              disabled={saving || !settings.auto_advance_on_interview_complete}
              onChange={(e) => void patch({ auto_advance_target_status: e.target.value })}
            />
          </div>
        </Section>

        <Section title={<span className="inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Stuck in stage</span>}>
          <p className="ggj-text-body vscode-text-secondary leading-relaxed">
            Flag applications that stay in the same stage longer than N days for reviewer follow-up. Persistence is saved
            here; background processing can be enabled later without changing this UI.
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border vscode-border"
              checked={settings.stuck_stage_enabled}
              disabled={saving}
              onChange={(e) => void patch({ stuck_stage_enabled: e.target.checked })}
            />
            <span className="ggj-text-body vscode-text-primary">Enable stuck-stage reviewer flag</span>
          </label>
          <div>
            <label className="ggj-text-meta vscode-text-tertiary">Days before flagging</label>
            <input
              type="number"
              min={1}
              max={365}
              className="input w-32 mt-1"
              value={settings.stuck_stage_days}
              disabled={saving || !settings.stuck_stage_enabled}
              onChange={(e) =>
                void patch({ stuck_stage_days: Math.min(365, Math.max(1, Number(e.target.value) || 7)) })
              }
            />
          </div>
        </Section>

        <PositionDefaultsSection orgId={orgId} canEdit={perms.isAdmin || perms.isOwner} />

        {saving ? <p className="ggj-text-meta vscode-text-tertiary">Saving…</p> : null}
      </div>
      )}
    </PermissionGate>
  );
}
